import Api from './api/Api.js';
import Application from './api/Application.js';
import Converters from './api/Converters.js';
import Devices from './api/Devices.js';
import Images from './api/Images.js';
import Localizations from './api/Localizations.js';
import Users from './api/Users.js';
import Inventory from './api/Inventory.js';
import Info from './api/Info.js';


function buildQueryParamName(param) {
	if (typeof param === 'string') {
		return param;
	} else if (Array.isArray(param)) {
		return param[0] + param.slice(1).map(p => '[' + p + ']').join('')
	}
}

export function createQueryString(queryParams, prefix = [], includeUndefinedValues = false) {
	let query = [];
	
	Object.keys(queryParams).forEach(param => {
    const newPrefix = prefix.concat([param])
    
    if (typeof queryParams[param] === "object") {
      const sub = createQueryString(queryParams[param], newPrefix)
      query.push(sub)
		} else if (includeUndefinedValues || queryParams[param] !== undefined) {
      const name = buildQueryParamName(newPrefix)
      query.push([encodeURIComponent(name), encodeURIComponent(queryParams[param])].join('='));
		}
	});
	
	query = query.filter(el => el !== "");
	
	return query.join('&');
}

export default {
	namespaced: true,
	state: {
		UILock: false,
		
		authToken: null,
		authTokenData: null,
		
		apiBaseUrl: null,
		
		requestQueue: [],
	},
	getters: {
		getAuthHeader: (state, getters, rootState) => {
			return {
				'X-Kabi-Token': state.authToken,
				'X-Kabi-Version': rootState.appVersion
			}
		}
	},
	mutations: {
		setApiBaseUrl: (state, url) => state.apiBaseUrl = url,
		setUILock: (state, UILock) => state.UILock = UILock,
		
		// auth
		setAuthToken: (state, authToken) => {
			state.authToken = authToken;
			window.forage.setItem(process.env.VUE_APP_AUTH_TOKEN_STORAGE_KEY, authToken)
				.catch(e => null)
		},
		setAuthTokenData: (state, data) => {
			state.authTokenData = data;
		},
		clearAuthToken: (state) => {
			state.authToken = null;
			state.authTokenData = null;
			window.forage.removeItem(process.env.VUE_APP_AUTH_TOKEN_STORAGE_KEY)
				.catch(e => null)
		},
		
		// queue
		setQueue: (state, queue) => state.requestQueue = queue,
		pushQueue: (state, item) => {
			if (item.signal) {
				item.signal = null
			}
			if (item.headers.Authorization) {
				item.headers.Authorization = ""
			}
			state.requestQueue.push(item);
		},
		popQueue: (state) => {
			state.requestQueue.shift();
		},
	},
	actions: {
		/**
		 *
		 * @param state
		 * @param commit
		 * @param dispatch
		 * @param actionPath
		 * @param method
		 * @param headers
		 * @param data
		 * @param UILock
		 * @param abortSignal
		 * @returns {Promise<Response>}
		 */
		apiRequest: ({state, commit, dispatch}, {
			actionPath,
			method,
			headers = {},
			data = undefined,
			UILock = false,
			abortSignal = null
		}) => {
			if (UILock) {
				commit('setUILock', true);
				
			}
			let options = {
				method: method,
				headers: headers,
				// credentials: 'include',
				// mode: 'no-cors',
				signal: abortSignal
			};
			
			if (data) {
				options.body = headers['Content-Type'] === 'application/json' ? JSON.stringify(data) : data
			}
			
			
			return fetch(state.apiBaseUrl + actionPath, options)
				.then(response => {
					
					if (UILock) {
						commit('setUILock', false);
					}
					
					let responseContentType = response.headers.get('content-type').split(';')[0];
					
					if (responseContentType === 'application/json') {
						return response.json();
					} else if (['image\/*', 'video\/*'].find(blobType => responseContentType.match(blobType))) {
						return response.blob();
					} else {
						return response.text();
					}
					
				})
				.then(async (json) => {
					if (json instanceof Blob) {
						return Promise.resolve(json);
					}
					if (json.status === 'ok') {
						
						if (json.data && json.data.token) {
							
							try {
								await dispatch('updateAuthToken', json.data.token)
							} catch (e) {
								json.error = {
									code: 0,
									en: 'received invalid token, ' + e
								}
								
							}
							
						}
						
						return json.data;
					}
					
					if (json.status === 'error') {
						throw json.error;
					}
					
					throw json;
				})
		},
		queuedApiRequest: async ({state, commit, getters, dispatch}, options = null) => {
			
			if (options) {
				commit('pushQueue', options);
			}
			
			let result;
			while (state.requestQueue.length !== 0) {
				result = null;
				try {
					let queuedOptions = JSON.parse(JSON.stringify(state.requestQueue[0]));//Object.assign({}, );
					
					console.log('try queued request with options', queuedOptions);
					
					if (queuedOptions.headers.Authorization) {
						Object.assign(queuedOptions.headers, getters.getAuthHeader);
					}
					
					console.log('after merge headers', queuedOptions)
					
					result = await dispatch('apiRequest', queuedOptions);
					commit('popQueue');
					
					// TODO: Add additional logic here
					// if (BOTTLE_ACTIONS.indexOf(queuedOptions.actionPath) && result.id) {
					//   commit('addParentIdToQueuedItems', {
					//     serial: queuedOptions.serial,
					//     parentId: result.id
					//   })
					// }
					
				} catch (error) {
					console.log('catch queue', state.requestQueue[0], error)
					if (typeof error === "string") {
						// break queue on network errors
						throw 'network error, added to queue'
					} else if (state.requestQueue.length === 0) {
						// throw api errors
						throw error //TODO: eventuell hier nicht throw error um queue nicht abzubrechen
					}
				}
				return result;
			}
			
		},
		checkTokenValidity({}, authToken) {
			return new Promise((resolve, reject) => {
				if (authToken) {
					let tokenParts = authToken.split('.');
					if (tokenParts.length === 3) {
						// 0 = headers, 1 = payload, 2 = signature
						let tokenPayload = JSON.parse(atob(tokenParts[1]));
						
						if (tokenPayload.exp * 1000 < Date.now()) {
							return reject('token expired');
						}
						
						return resolve({token: authToken, tokenPayload});
						
					} else {
						return reject('unwell formatted data')
					}
				} else {
					return reject('missing token')
				}
			})
		},
		updateAuthToken: async ({dispatch, commit}, authToken) => {
			try {
				let {token, tokenPayload} = dispatch("checkTokenValidity", authToken);
				commit('setAuthToken', token);
				commit('setAuthTokenData', tokenPayload);
			} catch (e) {
				commit('setAuthToken', null);
				commit('setAuthTokenData', null);
				throw e
			}
		},
	},
	modules: {
		Api, Application, Converters, Devices, Images, Localizations, Users, Inventory, Info
	}
}
